/**
 * The event model (plan.md §62.2), shared by the client that emits and the
 * API that validates.
 *
 * ONE definition, not two. The client writes these into IndexedDB while it has
 * no signal and posts them days later; if the two sides ever disagreed about a
 * field name, the events that matter most — the ones recorded in a field with
 * no reception — would be the ones silently dropped.
 *
 * Pure, like everything here: no clock, no randomness, no I/O. An event's id
 * and its `occurredAt` are supplied by the caller, which is what lets a test
 * replay a batch and get the same rollup every time.
 */
import { dayBucket, type DayBucket, type TimeZone } from './time.js';

/**
 * What is measured. Events, not page views.
 *
 * `act_save` and `act_unsave` are a PAIR: the signal is the net, because a
 * plan someone assembled and then dismantled is not the same as one they kept,
 * and only counting saves makes every act look more popular over time.
 *
 * `search` carries its result count because a search returning ZERO is the
 * single most valuable row here — it is a direct list of what people expected
 * to find and did not.
 */
export const EVENT_TYPES = [
  'attend',
  'bundle_download',
  'act_save',
  'act_unsave',
  'act_view',
  'search',
  'map_open',
  'map_pin',
  'ics_export',
  'plan_share',
  'plan_open',
  'session',
  'ticket_click',
  // A ticket saved ON THE PHONE (step 91): the rave and nothing of the ticket — the only thing the server learns.
  'ticket_saved',
] as const;

/** Where a "Buy tickets" button was pressed (the user, 2026-10-27: who pressed, when, how many). */
/** Where a "Buy tickets" press came from — `tickets`: the rave's own My tickets page (QA 2026-11-02). */
export const TICKET_PLACES = ['menu', 'announced', 'info', 'tickets'] as const;
export type TicketPlace = (typeof TICKET_PLACES)[number];

export type EventType = (typeof EVENT_TYPES)[number];

/** Events that name an act, and so roll up per (edition, act). */
export const ACT_EVENT_TYPES = ['act_save', 'act_unsave', 'act_view'] as const;
export type ActEventType = (typeof ACT_EVENT_TYPES)[number];

interface EventBase {
  /**
   * Generated on the CLIENT, and the idempotency key.
   *
   * A phone that syncs on a flaky connection will send the same batch twice.
   * The id is what makes the second one a no-op instead of doubling a crowd
   * count — so it must be stable from the moment the event is queued, not
   * assigned at send time.
   */
  id: string;
  /**
   * The client's clock, epoch ms.
   *
   * Not trusted and not discarded. The server records its own `receivedAt`
   * beside it: a phone with a wrong clock must not silently move a set into
   * another day, and a batch that arrives two days late is still true about
   * when it happened.
   */
  occurredAt: number;
}

export type AppEvent =
  | (EventBase & { type: 'attend' })
  | (EventBase & { type: 'bundle_download'; version: string })
  | (EventBase & { type: ActEventType; actSlug: string })
  | (EventBase & { type: 'search'; query: string; resultCount: number })
  | (EventBase & { type: 'map_open' })
  | (EventBase & { type: 'map_pin' })
  | (EventBase & { type: 'ics_export'; actCount: number })
  | (EventBase & { type: 'plan_share' | 'plan_open' })
  | (EventBase & { type: 'session'; online: boolean; installed: boolean })
  | (EventBase & { type: 'ticket_click'; place: TicketPlace })
  | (EventBase & { type: 'ticket_saved' });

/** A whole batch is refused past this, rather than truncated silently. */
export const MAX_EVENT_BATCH = 500;
/** Long enough for any real query; short enough that nothing can be smuggled. */
export const MAX_QUERY_LENGTH = 200;
const MAX_ID_LENGTH = 64;
const MAX_SLUG_LENGTH = 128;

const str = (v: unknown, max: number): string | null =>
  typeof v === 'string' && v.length > 0 && v.length <= max ? v : null;

const int = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v) && v >= 0 ? v : null;

/** A guard, not a check: the switch below relies on `type` being narrowed. */
function isEventType(value: unknown): value is EventType {
  return typeof value === 'string' && (EVENT_TYPES as readonly string[]).includes(value);
}

/**
 * Parse one event, keeping ONLY the fields its type declares.
 *
 * Rebuilding the object field by field rather than validating in place is the
 * privacy guarantee from §62.4 made structural: an IP address, a user agent or
 * a location cannot reach storage because there is no branch here that copies
 * an unrecognised key. A deny-list would grow a hole the first time someone
 * added a field.
 *
 * Returns null for anything malformed. One bad event never fails a batch —
 * dropping a single row is better than losing a phone's entire festival.
 */
export function parseEvent(value: unknown): AppEvent | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;

  const id = str(raw.id, MAX_ID_LENGTH);
  const occurredAt = int(raw.occurredAt);
  const type = raw.type;
  if (!id || occurredAt === null) return null;
  if (!isEventType(type)) return null;

  const base = { id, occurredAt };
  switch (type) {
    case 'attend':
    case 'map_open':
    case 'map_pin':
    case 'plan_share':
    case 'plan_open':
      return { ...base, type };

    case 'ticket_click': {
      const place = (TICKET_PLACES as readonly unknown[]).includes(raw.place) ? raw.place as TicketPlace : null;
      return place ? { ...base, type, place } : null;
    }

    // Nothing but that it happened — no code, no name, no picture (step 91).
    case 'ticket_saved':
      return { ...base, type };

    case 'bundle_download': {
      const version = str(raw.version, MAX_ID_LENGTH);
      return version ? { ...base, type, version } : null;
    }
    case 'act_save':
    case 'act_unsave':
    case 'act_view': {
      const actSlug = str(raw.actSlug, MAX_SLUG_LENGTH);
      return actSlug ? { ...base, type, actSlug } : null;
    }
    case 'search': {
      const query = str(raw.query, MAX_QUERY_LENGTH);
      const resultCount = int(raw.resultCount);
      return query !== null && resultCount !== null ? { ...base, type, query, resultCount } : null;
    }
    case 'ics_export': {
      const actCount = int(raw.actCount);
      return actCount === null ? null : { ...base, type, actCount };
    }
    case 'session': {
      if (typeof raw.online !== 'boolean' || typeof raw.installed !== 'boolean') return null;
      return { ...base, type, online: raw.online, installed: raw.installed };
    }
  }
  return null;
}

/**
 * Parse a batch, keeping what is valid and reporting what was not.
 *
 * Duplicate ids within one batch collapse here as well as at the database, so
 * a client that queued the same event twice cannot double-count even before
 * the unique index sees it.
 */
export function parseEventBatch(value: unknown): { events: AppEvent[]; rejected: number } {
  if (!Array.isArray(value)) return { events: [], rejected: 0 };

  const events: AppEvent[] = [];
  const seen = new Set<string>();
  let rejected = 0;

  for (const item of value.slice(0, MAX_EVENT_BATCH)) {
    const parsed = parseEvent(item);
    if (!parsed) { rejected++; continue; }
    if (seen.has(parsed.id)) { rejected++; continue; }
    seen.add(parsed.id);
    events.push(parsed);
  }
  return { events, rejected };
}

/** The act an event is about, if any. */
export function actSlugOf(event: AppEvent): string | null {
  return 'actSlug' in event ? event.actSlug : null;
}

// ---------------------------------------------------------------------------
// Rollups
// ---------------------------------------------------------------------------

/**
 * What a rollup is derived FROM.
 *
 * Deliberately not a Prisma row: this runs in the rollup job, in a test with
 * literals, and anywhere someone wants to check a published number against the
 * raw events. §62.5 — a rollup nobody can recompute is a number nobody can
 * defend.
 */
export interface RollupInputEvent {
  type: EventType;
  /** The instant the rollup buckets by. The job passes `occurredAt`. */
  at: Date;
  actSlug: string | null;
  /**
   * Who — a user id when signed in, otherwise a per-edition device id.
   * Never linked across festivals (§62.4). Used only to count distinct actors.
   */
  actorKey: string;
}

/** One rollup row. `day` and `actSlug` null mean "the whole edition". */
export interface RollupBucket {
  day: DayBucket | null;
  actSlug: string | null;
  counts: Partial<Record<EventType, number>>;
  /** Distinct actors that produced any event in this bucket. */
  actors: number;
}

function bump(
  map: Map<string, { bucket: RollupBucket; actors: Set<string> }>,
  day: DayBucket | null,
  actSlug: string | null,
  event: RollupInputEvent,
): void {
  const key = `${day ?? ''} ${actSlug ?? ''}`;
  let entry = map.get(key);
  if (!entry) {
    entry = { bucket: { day, actSlug, counts: {}, actors: 0 }, actors: new Set() };
    map.set(key, entry);
  }
  entry.bucket.counts[event.type] = (entry.bucket.counts[event.type] ?? 0) + 1;
  entry.actors.add(event.actorKey);
}

/**
 * Derive every rollup bucket from raw events. Pure, total and deterministic:
 * the same events in any order produce the same rows.
 *
 * Three shapes, which is what makes "most-saved act" one indexed read rather
 * than a scan of every save on the busiest day of the festival:
 *
 *   (null, null)      the edition
 *   (day,  null)      one day
 *   (null, actSlug)   one act
 *
 * Days are bucketed in the EDITION's timezone through the same `dayBucket` the
 * schedule uses, so a 00:30 save belongs to the day the visitor thinks it does.
 */
export function rollupEvents(
  events: readonly RollupInputEvent[],
  timeZone: TimeZone,
): RollupBucket[] {
  return rollupDeltas(events, timeZone).map(({ actorKeys, ...bucket }) => ({ ...bucket, actors: actorKeys.length }));
}

/** A bucket a batch of events touches: what to ADD to its counters, and who is in it. */
export interface RollupDelta {
  day: DayBucket | null;
  actSlug: string | null;
  counts: Partial<Record<EventType, number>>;
  /** The batch's distinct actors in this bucket — the job adds only those not counted before. */
  actorKeys: string[];
}

/**
 * The same buckets as `rollupEvents`, with the actors themselves instead of how many. The
 * rollup job ADDS these to the stored counters (2026-10-28: the totals are counters that only
 * go forward), so a person seen in an earlier batch is not counted twice.
 */
export function rollupDeltas(
  events: readonly RollupInputEvent[],
  timeZone: TimeZone,
): RollupDelta[] {
  const map = new Map<string, { bucket: RollupBucket; actors: Set<string> }>();

  for (const event of events) {
    const day = dayBucket(event.at, timeZone);
    bump(map, null, null, event);
    bump(map, day, null, event);
    if (event.actSlug) bump(map, null, event.actSlug, event);
  }

  return [...map.values()]
    .map(({ bucket, actors }) => ({ day: bucket.day, actSlug: bucket.actSlug, counts: bucket.counts, actorKeys: [...actors].sort() }))
    .sort(
      (a, b) =>
        (a.day ?? '').localeCompare(b.day ?? '') ||
        (a.actSlug ?? '').localeCompare(b.actSlug ?? ''),
    );
}

/**
 * How many people currently have this act in their plan.
 *
 * Saves minus unsaves, never below zero. The total alone is not the signal:
 * an act saved and then dropped by everyone would otherwise look as popular as
 * one nobody dropped, and would grow more popular the longer the festival ran.
 *
 * It can undercount — a save made before the retention window and an unsave
 * made after it leaves the pair unbalanced — so it is floored rather than
 * allowed to go negative.
 */
export function netSaves(counts: Partial<Record<EventType, number>>): number {
  return Math.max(0, (counts.act_save ?? 0) - (counts.act_unsave ?? 0));
}
